import { type Colors } from '@factorialco/deprecated-design-system'
import {
  ShiftManagementAreasLocationsEmployeesQuery,
  ShiftManagementAreasViewPageQuery,
  ShiftManagementMainPageQuery,
} from 'generated/resources/graphql'
import { compact, groupBy, uniq } from 'lodash'
import orderBy from 'lodash/orderBy'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

import workAreas from 'modules/locations/store/collections/workAreas'
import { type Duration, momentToDuration } from 'modules/shift_management/lib/hoursToDuration'
import { Leave } from 'modules/shift_management/lib/types'
import {
  minimumRestViolationsBetweenDays,
  type MinimumRestViolation,
} from 'modules/shift_management/lib/minimumRestBetweenDays'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import PlanningVersions from 'modules/shift_management/store/internal/collections/PlanningVersions'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import { NO_WORK_AREA } from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'

import ReferenceContract from './ReferenceContract'
import ShiftPermissions from './ShiftPermissions'

export type TShiftsRow =
  ShiftManagementMainPageQuery['shiftManagement']['employeesPermissionsConnection']['nodes'][number]

export type TWorkAreaShiftsRow =
  ShiftManagementAreasViewPageQuery['shiftManagement']['workAreasConnection']['nodes'][number]

export type TLocation =
  ShiftManagementAreasLocationsEmployeesQuery['locations']['locations'][number]
export type TEmployee =
  ShiftManagementAreasLocationsEmployeesQuery['shiftManagement']['employeesPermissions'][number]

type EmployeeJobSource = Pick<TShiftsRow, 'referenceContract'>

export const ShiftsRowType = {
  employee: 'employee',
  workArea: 'work_area',
} as const

type ShiftsRowType = (typeof ShiftsRowType)[keyof typeof ShiftsRowType]

interface UrlParams {
  id: string
}

interface Props {
  id: string
  type?: ShiftsRowType
  title: string
  job?: string
  maximumWeeklyHours?: number
  subtitle?: string
  avatar?: string
  shifts: Shifts
  leaves: Leaves
  holidays: Holidays
  restDays: RestDays
  planningVersions: PlanningVersions
  permissions: ShiftPermissions
  referenceContract?: ReferenceContract
  terminatedOn?: string | null
  key?: string
  urlParams?: UrlParams
  fragment?: TShiftsRow
}

class ShiftsRow {
  id: string
  _type: ShiftsRowType
  title: string
  job?: string
  maximumWeeklyHours?: number
  subtitle?: string
  avatar?: string
  shifts: Shifts
  leaves: Leaves
  holidays: Holidays
  restDays: RestDays
  planningVersions: PlanningVersions
  // TODO: Handle permissions in a different way for each row depending on employees, areas, etc
  permissions: ShiftPermissions
  // Use this to iterate or select rows to avoid null ids like "No work area" rows
  key: string
  urlParams?: UrlParams
  fragment?: TShiftsRow
  // TODO: Make this required once 'dev_shift_management_graphql_migration' is done
  referenceContract?: ReferenceContract
  terminatedOn?: string | null

  constructor(props: Props) {
    this.id = props.id
    this._type = props.type || ShiftsRowType.employee
    this.title = props.title
    this.job = props.job
    this.maximumWeeklyHours = props.maximumWeeklyHours
    this.subtitle = props.subtitle
    this.avatar = props.avatar
    this.shifts = props.shifts
    this.leaves = props.leaves
    this.holidays = props.holidays
    this.restDays = props.restDays
    this.planningVersions = props.planningVersions
    this.permissions = props.permissions
    this.referenceContract = props.referenceContract
    this.permissions = props.permissions
    this.key = props.key || this.id.toString()
    this.urlParams = props.urlParams
    this.terminatedOn = props.terminatedOn

    makeObservable(this, {
      isEmployee: computed,
      isWorkArea: computed,
      hasShifts: computed,
      employeeIds: computed,
      visibleShifts: computed,
      visibleShiftCollection: computed,
      orderedShifts: computed,
      nonBackupShifts: computed,
      plannedBreaksCollection: computed,
      canManage: computed,
      canCreateLeaves: computed,
      canApproveLeaves: computed,
      canDeleteLeaves: computed,
      hasNoneShiftManagementPlanning: computed,
      minimumRestBetweenDaysViolations: computed,
      minimumRestBetweenDaysShiftIds: computed,
    })
  }

  static buildFromGraphQL(
    shiftsRow: TShiftsRow,
    type: ShiftsRowType,
    hasLegacyJobCatalogFeature = false
  ): ShiftsRow {
    const avatar = shiftsRow.avatar?.url || ''
    const fullName = shiftsRow.fullName || ''

    return new ShiftsRow({
      id: shiftsRow.id,
      type,
      title: fullName,
      maximumWeeklyHours: shiftsRow.referenceContract?.maximumWeeklyHours || undefined,
      avatar,
      job: this.employeeJob(shiftsRow, hasLegacyJobCatalogFeature),
      shifts: Shifts.buildFromGraphQL({
        shifts: shiftsRow.shifts,
        employeeFullName: fullName,
        employeeAvatar: avatar,
        employeeLocationId: shiftsRow.location?.id || null,
      }),
      leaves: Leaves.buildFromGraphQL(
        shiftsRow.leaves,
        shiftsRow.avatar?.url || '',
        shiftsRow.fullName
      ),
      holidays: Holidays.buildFromGraphQL(shiftsRow.location),
      restDays: RestDays.build(
        shiftsRow.restDays.map((restDay) => ({
          id: restDay.id,
          employee_id: restDay.employee?.id ?? '0',
          date: restDay.date,
          state: restDay.state,
          timezone: restDay.timezone || '',
          employeeFullName: restDay.employee?.fullName || '',
          employeeAvatar: restDay.employee?.avatar?.url || '',
        }))
      ),
      planningVersions: PlanningVersions.buildFromGraphQL(shiftsRow.planningVersions),
      permissions: ShiftPermissions.buildFromGraphQL(shiftsRow) || {},
      referenceContract: shiftsRow.referenceContract
        ? ReferenceContract.buildFromGraphQL(shiftsRow.referenceContract)
        : undefined,
      terminatedOn: shiftsRow.terminatedOn,
      fragment: shiftsRow,
    })
  }

  static buildFromWorkArea(
    workArea: TWorkAreaShiftsRow,
    location: TLocation,
    employeesPermissions: TEmployee[]
  ): ShiftsRow {
    const workAreaId = workArea.id ?? '0'
    const locationId = workArea.location?.id ?? '0'
    const employeesByLocationId = groupBy(employeesPermissions, 'location.id')
    const employeesById = groupBy(employeesPermissions, 'id')
    const workAreaEmployees = employeesByLocationId[locationId] || []
    const shifts = workArea.id ? workArea.shifts : location.shiftsWithoutWorkArea || []
    const shiftsEmployees = compact(shifts.flatMap((shift) => employeesById[shift.employeeId]?.[0]))
    const employees = compact(uniq([...workAreaEmployees, ...shiftsEmployees]))
    const canCreateShifts = employees.some(({ permissions }) => permissions.create)
    const planningVersions = employees.flatMap((employee) => employee.planningVersions)

    return new ShiftsRow({
      id: workAreaId,
      type: ShiftsRowType.workArea,
      key: `${workArea.location?.id}-${workArea.id || NO_WORK_AREA}`,
      title: workArea.name,
      subtitle: location.name,
      shifts: new Shifts(
        compact(
          shifts.map((shift) => {
            const employee = employeesById[shift.employeeId]?.[0]
            if (!employee) return null

            return Shift.buildFromGraphQL({
              shift,
              employeeFullName: employee.fullName || '',
              employeeAvatar: '', // Areas view doesn't show avatars
              employeeLocationId: workArea?.location?.id || null,
            })
          })
        )
      ),
      holidays: Holidays.buildFromGraphQL(location),
      // We won't handle leaves and rest days in the work area view
      leaves: Leaves.buildFromGraphQL([], '', ''),
      restDays: RestDays.build([]),
      planningVersions: PlanningVersions.buildFromGraphQL(planningVersions),
      urlParams: workArea.id ? undefined : { id: locationId },
      permissions: ShiftPermissions.buildFromGraphQL({
        id: '0',
        permissions: {
          create: canCreateShifts,
          createLeave: false,
          approveLeave: false,
          deleteLeave: false,
        },
      }),
    })
  }

  get isEmployee(): boolean {
    return this._type === ShiftsRowType.employee
  }

  get isWorkArea(): boolean {
    return this._type === ShiftsRowType.workArea
  }

  static employeeJob(shiftsRow: EmployeeJobSource, hasLegacyJobCatalogFeature = false): string {
    const { jobTitle, jobCatalogLevel, jobCatalogTreeLevel } = shiftsRow.referenceContract || {}

    if (jobTitle) return jobTitle

    if (!hasLegacyJobCatalogFeature) {
      const jobCatalogTreeRole = jobCatalogTreeLevel?.ancestor?.name || ''
      const jobCatalogTreeLevelName = jobCatalogTreeLevel?.name || ''

      if (jobCatalogTreeRole && jobCatalogTreeLevelName) {
        return `${jobCatalogTreeRole}, ${jobCatalogTreeLevelName}`
      }

      if (jobCatalogTreeRole) return jobCatalogTreeRole

      if (jobCatalogTreeLevelName) return jobCatalogTreeLevelName
    }

    const jobRole = jobCatalogLevel?.roleName || ''
    const jobLevel = jobCatalogLevel?.isDefault ? '' : jobCatalogLevel?.name || ''

    return jobRole && jobLevel ? `${jobRole}, ${jobLevel}` : jobRole
  }

  get hasShifts(): boolean {
    return !!this.shifts.shiftIds.length
  }

  get employeeIds(): string[] {
    if (this.isEmployee) return [this.id]

    // Use this.shifts instead of this.employees because we want to refresh or consider only those employees with shifts in the row
    return uniq([...this.shifts.employeeIds, ...this.restDays.employeeIds])
  }

  // For the work area view, this will be used to get the location id
  get keyReference(): string {
    const firstKeyItem = this.key.split('-')[0]
    return String(firstKeyItem)
  }

  get isTerminated(): boolean {
    if (!this.terminatedOn) return false

    return stringToDate(this.terminatedOn, 'backend').isBefore(moment(), 'day')
  }

  get terminationDate(): moment.Moment | null | undefined {
    return this.terminatedOn ? stringToDate(this.terminatedOn, 'backend') : null
  }

  get visibleShifts(): Shift[] {
    const { shifts } = this.shifts
    return shifts.filter((shift: Shift) => {
      const planningVersion = this.planningVersions.planningVersionWithinDate(
        shift.employeeId,
        shift.date
      )

      return (
        planningVersion?.isShiftManagementTool &&
        (!this.terminationDate || shift.date <= this.terminationDate)
      )
    })
  }

  get visibleShiftCollection(): Shifts {
    return new Shifts(this.visibleShifts)
  }

  get orderedShifts(): Shift[] {
    return orderBy(this.visibleShifts, ['_startAt', '_endAt'], ['asc', 'asc'])
  }

  get nonBackupShifts(): Shift[] {
    return this.shifts.shifts.filter((s) => !s.isBackup)
  }

  get plannedBreaksCollection(): PlannedBreaks {
    return this.visibleShiftCollection.plannedBreaksCollection
  }

  shiftsByDate(date: moment.Moment, view: View): Shift[] {
    return this.visibleShiftCollection.filterByDate(date, view)
  }

  orderedShiftsByDate(date: moment.Moment, view: View): Shift[] {
    return orderBy(this.shiftsByDate(date, view), ['_startAt', '_endAt'], ['asc', 'asc'])
  }

  nightShiftsPeriodBefore(date: moment.Moment): Shift[] {
    return this.visibleShiftCollection.filterByNightShiftEndDate(date)
  }

  orderedNightShiftsPeriodBefore(date: moment.Moment): Shift[] {
    return orderBy(this.nightShiftsPeriodBefore(date), ['_endAt', '_startAt'], ['asc', 'asc'])
  }

  // FIXME: We will remove this once 'dev_shift_management_bank_of_hours' is done
  // And we will use hoursPerRangeAsMilliseconds instead
  //
  // Givin a period (week, month, 4-weeks) from firstDay to lastDay
  hoursPerRange(
    firstDay: moment.Moment,
    lastDay: moment.Moment,
    plannedBreaks: PlannedBreaks
  ): Duration {
    // (1) Duration of shifts within the Range
    const withinRange = this.visibleShiftCollection.filterByNotBackupAndWithinRange(
      firstDay,
      lastDay
    )
    const withinRangeDuration = withinRange.duration()

    // (2) Duration of the proportional part of shifts that
    // jump from one Range to the next

    const overnightToNext = this.visibleShiftCollection.filterByOvernightToNextRange(lastDay)
    const toNextDuration = overnightToNext.durationOvernightToNextWeek()

    // (3) Duration of the proportional part of shifts that
    // come from the previous Range to the current one
    const overnightFromPrevious =
      this.visibleShiftCollection.filterByOvernightFromPreviousRange(firstDay)
    const fromPreviousDuration = overnightFromPrevious.durationOvernightFromPreviousWeek()

    // (4) Unpaid planned breaks to subtract
    const allShiftIds = withinRange.shiftIds
      .concat(overnightToNext.shiftIds)
      .concat(overnightFromPrevious.shiftIds)

    // TODO: Use plannedBreaksCollection once 'dev_shift_management_graphql_migration' is done
    const selectedPlannedBreaks = this.plannedBreaksCollection.plannedBreaks.length
      ? this.plannedBreaksCollection.filterByShifts(allShiftIds)
      : plannedBreaks.filterByShifts(allShiftIds)
    const plannedBreaksMinutes = selectedPlannedBreaks.reduce((acc, plannedBreak) => {
      const { duration, breakConfigurationPaid } = plannedBreak
      if (!breakConfigurationPaid && duration) return acc + duration
      return acc
    }, 0)

    const plannedBreaksDuration = moment.duration(plannedBreaksMinutes, 'minutes')

    // (1) + (2) + (3) - (4)
    const totalDuration = withinRangeDuration
      .add(toNextDuration)
      .add(fromPreviousDuration)
      .subtract(plannedBreaksDuration)

    return momentToDuration(totalDuration)
  }

  private getShiftsAndDurationsForRange(
    firstDay: moment.Moment,
    lastDay: moment.Moment
  ): {
    shifts: Shift[]
    withinRangeDuration: moment.Duration
    toNextDuration: moment.Duration
    fromPreviousDuration: moment.Duration
  } {
    const withinRange = this.visibleShiftCollection.filterByNotBackupAndWithinRange(
      firstDay,
      lastDay
    )
    const withinRangeDuration = withinRange.duration()

    const overnightToNext = this.visibleShiftCollection.filterByOvernightToNextRange(lastDay)
    const toNextDuration = overnightToNext.durationOvernightToNextWeek()

    const overnightFromPrevious =
      this.visibleShiftCollection.filterByOvernightFromPreviousRange(firstDay)
    const fromPreviousDuration = overnightFromPrevious.durationOvernightFromPreviousWeek()

    return {
      shifts: [...withinRange.shifts, ...overnightToNext.shifts, ...overnightFromPrevious.shifts],
      withinRangeDuration,
      toNextDuration,
      fromPreviousDuration,
    }
  }

  shiftsPerRange(firstDay: moment.Moment, lastDay: moment.Moment): Shift[] {
    return this.getShiftsAndDurationsForRange(firstDay, lastDay).shifts
  }

  hoursPerRangeAsMilliseconds(
    firstDay: moment.Moment,
    lastDay: moment.Moment,
    plannedBreaks: PlannedBreaks
  ): number {
    const { withinRangeDuration, toNextDuration, fromPreviousDuration, shifts } =
      this.getShiftsAndDurationsForRange(firstDay, lastDay)

    // TODO: Use plannedBreaksCollection once 'dev_shift_management_graphql_migration' is done
    const shiftIds = shifts.map((s) => s.id)
    const selectedPlannedBreaks = this.plannedBreaksCollection.plannedBreaks.length
      ? this.plannedBreaksCollection.filterByShifts(shiftIds)
      : plannedBreaks.filterByShifts(shiftIds)

    const plannedBreaksMinutes = selectedPlannedBreaks.reduce((acc, plannedBreak) => {
      const { duration, breakConfigurationPaid } = plannedBreak
      if (!breakConfigurationPaid && duration) return acc + duration
      return acc
    }, 0)

    const plannedBreaksDuration = moment.duration(plannedBreaksMinutes, 'minutes')

    const totalDuration = withinRangeDuration
      .add(toNextDuration)
      .add(fromPreviousDuration)
      .subtract(plannedBreaksDuration)

    return totalDuration.asMilliseconds()
  }

  addShifts(shifts: Shift[]): ShiftsRow {
    this.shifts.addShifts(shifts)

    return new ShiftsRow({ ...this, shifts: this.shifts })
  }

  resetShifts(shifts: Shift[]): ShiftsRow {
    this.shifts = new Shifts(shifts)

    return new ShiftsRow({ ...this, shifts: this.shifts })
  }

  resetLeaves(leaves: Leave[]): ShiftsRow {
    this.leaves = new Leaves(leaves)

    return new ShiftsRow({ ...this, leaves: this.leaves })
  }

  addRestDays(restDays: RestDay[]): ShiftsRow {
    this.restDays.addRestDays(restDays)

    return new ShiftsRow({ ...this, restDays: this.restDays })
  }

  destroyRestDay(restDay: RestDay): ShiftsRow {
    this.restDays.destroyRestDay(restDay.id)

    return new ShiftsRow({ ...this, restDays: this.restDays })
  }

  destroyRestDays(restDays: RestDay[]): ShiftsRow {
    this.restDays.destroyRestDays(restDays.map((restDay) => restDay.id))

    return new ShiftsRow({ ...this, restDays: this.restDays })
  }

  destroyShift(shift: Shift): ShiftsRow {
    this.shifts.destroyShift(shift.id)

    return new ShiftsRow({ ...this, shifts: this.shifts })
  }

  destroyShifts(shifts: Shift[]): ShiftsRow {
    const result = this.shifts.destroyShifts(shifts.map((shift) => shift.id))

    return new ShiftsRow({ ...this, shifts: result })
  }

  hasDrafts() {
    const drafts = this.shifts.filterByDraft()

    return !!drafts.length
  }

  get canManage(): boolean {
    return !!this.permissions.create
  }

  get canCreateLeaves(): boolean {
    return !!this.permissions.createLeave
  }

  get canApproveLeaves(): boolean {
    return !!this.permissions.approveLeave
  }

  get canDeleteLeaves(): boolean {
    return !!this.permissions.deleteLeave
  }

  canManageOn(date: moment.Moment): boolean | undefined {
    return (
      this.canManage &&
      (this.planningVersions.planningVersionWithinDate(this.id, date)?.isShiftManagementTool ||
        this.employeeIds.some(
          (employeeId) =>
            this.planningVersions.planningVersionWithinDate(employeeId, date)?.isShiftManagementTool
        ))
    )
  }

  get hasNoneShiftManagementPlanning(): boolean {
    return this.planningVersions.filterByPlanningTool(PLANNING_TOOL.shiftManagement).length === 0
  }

  get minimumRestBetweenDaysViolations(): MinimumRestViolation[] {
    const requiredHoursInCents = this.referenceContract?.minRestMinutesBetweenDays || 0
    const requiredMinutes = Math.round((requiredHoursInCents / 100) * 60)
    const shifts = this.visibleShifts.filter((shift) => !shift.isBackup)
    return minimumRestViolationsBetweenDays(shifts, requiredMinutes)
  }

  minimumRestBetweenDaysViolationsInRange(
    startDay: moment.Moment,
    endDay: moment.Moment
  ): MinimumRestViolation[] {
    return this.minimumRestBetweenDaysViolations.filter((violation) =>
      violation.violationDay.isBetween(startDay, endDay, 'day', '[]')
    )
  }

  get minimumRestBetweenDaysShiftIds(): string[] {
    return this.minimumRestBetweenDaysViolations.map((violation) => violation.shiftId)
  }

  minimumRestBetweenDaysShiftIdsInRange(startDay: moment.Moment, endDay: moment.Moment): string[] {
    return this.minimumRestBetweenDaysViolationsInRange(startDay, endDay).map(
      (violation) => violation.shiftId
    )
  }

  isDisabled(view: View, date: moment.Moment): boolean {
    // In work areas, for the moment cells are never disabled, only empty
    if (view.isByAreas) return false

    const planningVersion = this.planningVersions.planningVersionWithinDate(this.id, date)

    return (
      (!!planningVersion && !planningVersion.isShiftManagementTool) ||
      (!!this.terminationDate && date > this.terminationDate)
    )
  }

  isRowArchivedWorkArea(view: View): boolean {
    if (view.isByEmployees) return false

    const workArea = workAreas.find({ id: this.id })

    if (!workArea) return false

    return workArea.isArchived
  }

  updateShiftsColor(defaultShiftId: string, color: Colors): ShiftsRow {
    const shiftsToColorIds = this.shifts.filterByDefaultShift(defaultShiftId).map((s) => s.id)
    const shifts = this.shifts.updateShiftsColor(shiftsToColorIds, color)

    return new ShiftsRow({ ...this, shifts })
  }

  lastNightShift(date: moment.Moment): Shift | null {
    if (!this.visibleShifts.length) return null

    return this.visibleShifts.reduce((lastNightShift: Shift | null, shift) => {
      if (
        shift.isBeginNightShift(date) &&
        (!lastNightShift || shift.currentEndDate.isAfter(lastNightShift.currentEndDate))
      ) {
        lastNightShift = shift
      }

      return lastNightShift
    }, null)
  }

  hoursInTheNextPeriod(date: moment.Moment): number {
    return Number(this.lastNightShift(date)?.currentEndDate.format('HH')) || 0
  }

  firstShift(date: moment.Moment): Shift | null {
    if (!this.visibleShifts.length) return null

    return this.visibleShifts.reduce((firstShift: Shift | null, shift) => {
      if (
        !shift.isEndNightShift(date) &&
        (!firstShift || shift.currentStartDate.isBefore(firstShift.currentStartDate))
      ) {
        firstShift = shift
      }

      return firstShift
    }, null)
  }

  startingHour(date: moment.Moment): number | null {
    const value = Number(this.firstShift(date)?.currentStartDate.format('HH'))
    return isNaN(value) ? null : value
  }
}

export default ShiftsRow
